import { Alert, App as AntApp, Button, Card, Descriptions, Popconfirm, Select, Space, Switch, Typography } from "antd";
import { useCallback, useState } from "react";
import { api, errorText } from "../api";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

/** `GET /api/console/tenant` */
export interface TenantSettings {
  tenantId: string;
  displayName: string;
  status: string;
  widgetKey: string | null;
  timeZone: string;
  locale: string;
  currency: string;
  hosts: string[];
  widgetEnabled: boolean;
}

/** `GET /api/console/tenant/widget-loader` (paket 4.0); widget build edilmemişse sürüm alanları `null`. */
export interface WidgetLoader {
  appOrigin: string;
  src: string;
  version: string | null;
  versionedSrc: string | null;
  integrity: string | null;
}

/** Mağaza sayfasına eklenecek etiket. iframe ve API, loader'ın yüklendiği origin'den gelir (ADR 011). */
export function installSnippet(widgetKey: string, appOrigin: string): string {
  return `<script async src="${appOrigin}/w/salesos.js" data-tenant-key="${widgetKey}"></script>`;
}

/**
 * Sürümü sabit etiket (tehdit modeli E3): tarayıcı, içeriği SRI değeriyle eşleşmeyen loader'ı çalıştırmaz. Yeni loader
 * sürümü etiketin güncellenmesini ister.
 */
export function pinnedSnippet(widgetKey: string, src: string, integrity: string): string {
  return `<script async src="${src}" integrity="${integrity}" crossorigin="anonymous" `
    + `data-tenant-key="${widgetKey}"></script>`;
}

/** Konsol console.<domain>'de, widget app.<domain>'de çalışır; yerelde ikisi de backend'dir. */
function appOrigin(): string {
  const { protocol, hostname, port } = window.location;
  if (hostname.startsWith("console.")) {
    return `${protocol}//app.${hostname.slice("console.".length)}${port ? `:${port}` : ""}`;
  }
  return "http://localhost:8080";
}

export function SettingsPage() {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { message } = AntApp.useApp();
  const { data: settings, error, reload } = useResource(
    useCallback(() => api.get<TenantSettings>("/api/console/tenant"), []));
  const { data: loader } = useResource(
    useCallback(() => api.get<WidgetLoader>("/api/console/tenant/widget-loader"), []));
  // Düzenlenmekte olan liste; kaydedilene kadar sunucudaki listenin yerine gösterilir.
  const [edited, setEdited] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const hosts = edited ?? settings?.hosts ?? [];

  async function saveHosts() {
    setSaving(true);
    try {
      await api.put<{ hosts: string[] }>("/api/console/tenant/domains", { hosts });
      setEdited(null);
      reload();
      message.success("İzinli domain'ler kaydedildi");
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  async function setWidget(enabled: boolean) {
    try {
      await api.put("/api/console/tenant/widget", { enabled });
      message.success(enabled ? "Widget açıldı" : "Widget kapatıldı");
      reload();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!settings) {
    return null;
  }
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Mağaza ayarları</Typography.Title>
      <Card title="Mağaza">
        <Descriptions column={1} items={[
          { key: "name", label: "Ad", children: settings.displayName },
          { key: "id", label: "Kimlik", children: settings.tenantId },
          { key: "status", label: "Durum", children: settings.status === "ACTIVE" ? "Aktif" : "Askıya alınmış" },
          { key: "tz", label: "Saat dilimi", children: settings.timeZone },
          { key: "currency", label: "Para birimi", children: settings.currency },
        ]} />
      </Card>

      <Card title="Widget">
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Space>
            <Popconfirm
              title={settings.widgetEnabled ? "Widget kapatılsın mı?" : "Widget açılsın mı?"}
              description={settings.widgetEnabled
                ? "Asistan bütün sitelerinizde birkaç saniye içinde kaybolur; açık görüşmeler kapanır."
                : "Asistan izinli sitelerinizde yeniden görünür."}
              okText={settings.widgetEnabled ? "Kapat" : "Aç"}
              cancelText="Vazgeç"
              disabled={!manage}
              onConfirm={() => setWidget(!settings.widgetEnabled)}>
              <Switch checked={settings.widgetEnabled} disabled={!manage} aria-label="Widget açık" />
            </Popconfirm>
            <Typography.Text>{settings.widgetEnabled ? "Widget açık" : "Widget kapalı"}</Typography.Text>
          </Space>
          {!settings.widgetEnabled && (
            <Alert type="warning" showIcon title="Widget kapalı: müşteriler asistanı görmüyor." />
          )}
          {settings.widgetKey && (
            <>
              <Typography.Text strong>Kurulum etiketi</Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Bu etiketi sitenizin bütün sayfalarına, kapanış &lt;/body&gt; etiketinden önce ekleyin. Anahtar gizli
                değildir; widget yalnızca aşağıdaki izinli domain'lerde çalışır.
              </Typography.Paragraph>
              <Typography.Paragraph code copyable>
                {installSnippet(settings.widgetKey, loader?.appOrigin ?? appOrigin())}
              </Typography.Paragraph>
              {loader?.versionedSrc && loader.integrity && (
                <>
                  <Typography.Text strong>Sürümü sabit etiket (SRI)</Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Yalnızca bu sürümü yükler; dosya değiştirilirse tarayıcı çalıştırmaz. Yeni sürüm yayınlandığında
                    etiketi buradan güncellemeniz gerekir. Sürüm: {loader.version}
                  </Typography.Paragraph>
                  <Typography.Paragraph code copyable>
                    {pinnedSnippet(settings.widgetKey, loader.versionedSrc, loader.integrity)}
                  </Typography.Paragraph>
                </>
              )}
            </>
          )}
        </Space>
      </Card>

      <Card title="İzinli domain'ler">
        <Typography.Paragraph type="secondary">
          Widget yalnızca bu adreslerde açılır. Her alt alan adını ayrı ekleyin (ör. www.magaza.com ve magaza.com).
        </Typography.Paragraph>
        <Space.Compact style={{ width: "100%" }}>
          <Select mode="tags" value={hosts} onChange={setEdited} disabled={!manage} style={{ width: "100%" }}
            tokenSeparators={[",", " "]} placeholder="www.magaza.com" aria-label="İzinli domain'ler" open={false} />
          {manage && <Button type="primary" onClick={saveHosts} loading={saving}>Kaydet</Button>}
        </Space.Compact>
      </Card>
    </Space>
  );
}
